import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { collection, documentId, getDocs, query, where } from "firebase/firestore";
import PageHeader from "../components/PageHeader";
import { db, firebaseEnv } from "../firebase";
import { error, info } from "../logger";

type SurveyDiagRow = {
  id: string;
  title: string;
  status: string;
  userId: string | null;
  ownerId: string | null;
  userEmail: string | null;
  ownerEmail: string | null;
  issues: string[];
};

type StatusGroup = "published" | "unpublished" | "deleted";

const STATUS_ORDER: StatusGroup[] = ["published", "unpublished", "deleted"];

function normalizeStatus(status: string): StatusGroup | null {
  if (status === "published" || status === "unpublished" || status === "deleted") {
    return status;
  }
  return null;
}

function inspectSurveyShape(id: string, data: any): SurveyDiagRow {
  const issues: string[] = [];
  const userId = data?.userId ?? null;
  const ownerId = data?.ownerId ?? null;
  const roles = data?.roles && typeof data.roles === "object" ? data.roles : null;
  const memberIds = Array.isArray(data?.memberIds) ? data.memberIds : null;
  const json = data?.json;

  if (!userId) issues.push("missing userId");
  if (!ownerId) issues.push("missing ownerId");
  if (userId && ownerId && userId !== ownerId) issues.push("userId/ownerId mismatch");

  if (!roles) {
    issues.push("missing roles map");
  } else {
    const ownerEntries = Object.entries(roles).filter(([, role]) => role === "owner");
    if (ownerEntries.length === 0) issues.push("roles has no owner entry");
    if (ownerEntries.length > 1) issues.push("roles has multiple owner entries");
    if (userId && roles[userId] !== "owner") issues.push("roles[userId] is not owner");
    if (ownerId && roles[ownerId] !== "owner") issues.push("roles[ownerId] is not owner");
  }

  if (!memberIds) {
    issues.push("missing memberIds");
  } else {
    if (userId && !memberIds.includes(userId)) issues.push("memberIds missing userId");
    if (ownerId && !memberIds.includes(ownerId)) issues.push("memberIds missing ownerId");
  }

  if (!json || typeof json !== "object") {
    issues.push("missing json object");
  } else if (!Array.isArray(json.pages)) {
    issues.push("json.pages missing");
  }

  return {
    id,
    title: data?.title ?? "(Untitled)",
    status: data?.status ?? "(missing)",
    userId,
    ownerId,
    userEmail: null,
    ownerEmail: null,
    issues,
  };
}

async function loadUserEmails(uids: string[]) {
  const uniqueUids = Array.from(new Set(uids.filter(Boolean)));
  const emailByUid = new Map<string, string | null>();

  for (let i = 0; i < uniqueUids.length; i += 10) {
    const chunk = uniqueUids.slice(i, i + 10);
    const snap = await getDocs(query(collection(db, "users"), where(documentId(), "in", chunk)));
    snap.docs.forEach((docSnap) => {
      const data = docSnap.data() as any;
      emailByUid.set(docSnap.id, data?.email ?? null);
    });
    chunk.forEach((uid) => {
      if (!emailByUid.has(uid)) emailByUid.set(uid, null);
    });
  }

  return emailByUid;
}

export default function DiagSurveyShapes() {
  const [rows, setRows] = useState<SurveyDiagRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrMsg(null);
    try {
      const snap = await getDocs(collection(db, "surveys"));
      const baseRows = snap.docs
        .map((docSnap) => inspectSurveyShape(docSnap.id, docSnap.data()))
        .sort((a, b) => b.issues.length - a.issues.length || a.title.localeCompare(b.title));
      const emailByUid = await loadUserEmails(
        baseRows.flatMap((row) => [row.userId, row.ownerId]).filter((uid): uid is string => Boolean(uid))
      );
      const nextRows = baseRows.map((row) => ({
        ...row,
        userEmail: row.userId ? emailByUid.get(row.userId) ?? null : null,
        ownerEmail: row.ownerId ? emailByUid.get(row.ownerId) ?? null : null,
      }));
      setRows(nextRows);
      info("[Diag] Survey shape scan complete:", {
        env: firebaseEnv,
        total: nextRows.length,
        flagged: nextRows.filter((row) => row.issues.length > 0).length,
      });
    } catch (e: any) {
      error("[Diag] Survey shape scan failed:", e);
      setErrMsg(e?.message || "Failed to load survey diagnostics.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const flagged = useMemo(() => rows.filter((row) => row.issues.length > 0), [rows]);
  const grouped = useMemo(
    () =>
      STATUS_ORDER.map((status) => ({
        status,
        rows: flagged.filter((row) => normalizeStatus(row.status) === status),
      })),
    [flagged]
  );

  return (
    <Box sx={{ p: 2 }}>
      <PageHeader
        title="Survey Shape Diagnostics"
        subtitle={`Read-only scan of surveys in the ${firebaseEnv} Firebase environment`}
        actions={<Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        This admin-only page is read-only. It reports legacy survey-shape problems so you can fix documents by hand.
      </Alert>

      {errMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errMsg}
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
        <Chip label={`Environment: ${firebaseEnv}`} color="primary" variant="outlined" />
        <Chip label={`Total surveys: ${rows.length}`} variant="outlined" />
        <Chip label={`Flagged: ${flagged.length}`} color={flagged.length > 0 ? "warning" : "success"} variant="outlined" />
      </Stack>

      {loading ? (
        <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : flagged.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography>No survey shape issues found.</Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {grouped.map(({ status, rows: groupRows }) => (
            <Paper key={status} variant="outlined" sx={{ p: 2.5 }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ justifyContent: "space-between", flexWrap: "wrap" }}>
                  <Typography variant="h6" sx={{ textTransform: "capitalize" }}>
                    {status}
                  </Typography>
                  <Chip label={`${groupRows.length} flagged`} color={groupRows.length > 0 ? "warning" : "default"} size="small" />
                </Stack>

                {groupRows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No flagged surveys in this status.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {groupRows.map((row) => (
                      <Paper key={row.id} variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1}>
                          <Typography variant="h6">{row.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {row.id}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            status={row.status} userId={row.userId ?? "null"} ownerId={row.ownerId ?? "null"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            userEmail={row.userEmail ?? "unknown"} ownerEmail={row.ownerEmail ?? "unknown"}
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                            {row.issues.map((issue) => (
                              <Chip key={issue} label={issue} color="warning" size="small" />
                            ))}
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}
